/*
 * EG Gift Progress Bar
 * --------------------
 * Hydrates `<eg-gift-progress-bar>` with live cart state. The parent block
 * renders the initial fill width / status text server-side from Liquid;
 * this JS takes over after first paint and updates everything reactively
 * on the theme's `cart:update` event. Each `<li data-eg-gift-progress-tier>`
 * child block carries its own `data-threshold-cents` and `data-label`.
 */

const EVENT_CART_UPDATE = 'cart:update';
const FALLBACK_LOCALE = 'de-DE';
const FALLBACK_CURRENCY = 'EUR';

class EgGiftProgressBar extends HTMLElement {
  /** @type {number} */ #cartTotalCents = 0;
  /** @type {number} */ #maxThresholdCents = 0;
  /** @type {Intl.NumberFormat | null} */ #moneyFormatter = null;
  /** @type {AbortController | null} */ #ac = null;
  /** @type {Record<string, string>} */ #localeCache = {};
  /** @type {boolean} */ #collapsed = false;

  connectedCallback() {
    this.#cartTotalCents = parseInt(this.dataset.cartTotalCents || '0', 10);
    const currency = this.dataset.cartCurrency || FALLBACK_CURRENCY;
    const locale =
      document.documentElement.getAttribute('lang') ||
      navigator.language ||
      FALLBACK_LOCALE;
    try {
      this.#moneyFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
      });
    } catch (_err) {
      this.#moneyFormatter = new Intl.NumberFormat(FALLBACK_LOCALE, {
        style: 'currency',
        currency: FALLBACK_CURRENCY,
      });
    }

    this.#ac = new AbortController();

    // Restore the session's collapse preference as early as possible (on
    // element upgrade). State lives in sessionStorage, not the server markup,
    // so a previously-collapsed bar may paint expanded for one frame on a full
    // reload before this runs — acceptable for a session-scoped UI preference.
    this.#collapsed = this.#readStoredCollapsed();
    this.#applyCollapsed();

    this.#computeMaxThreshold();
    this.#renderNotches();
    this.#applyCartTotal(this.#cartTotalCents, this.#cartTotalCents);

    document.addEventListener(EVENT_CART_UPDATE, this.#onCartUpdate, {
      signal: this.#ac.signal,
    });
    // Delegated so it survives the cart's section morphs (the button DOM is
    // replaced, but the host element — and this listener — persists).
    this.addEventListener('click', this.#onToggleClick, {
      signal: this.#ac.signal,
    });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  #onCartUpdate = (event) => {
    const detail = event.detail || {};
    const total = detail?.resource?.total_price;
    if (typeof total !== 'number') return;
    const prev = this.#cartTotalCents;
    this.#cartTotalCents = total;
    this.dataset.cartTotalCents = String(total);

    /*
     * cart-items-component dispatches CartUpdateEvent BEFORE running
     * morphSection (see assets/component-cart-items.js). The morph in
     * 'hydration' mode then replaces the bar/status DOM with the
     * server-rendered output, which can blow away our JS-set state.
     * Additionally, static-block `block.blocks` introspection in the
     * parent block's Liquid does not appear to populate in partial
     * section re-renders, so the server output may have an empty bar
     * (no fill, no notches, no status). We re-hydrate from the DOM on
     * the next microtask so we run AFTER morph and own the final state.
     */
    queueMicrotask(() => {
      this.#computeMaxThreshold();
      this.#renderNotches();
      this.#applyCartTotal(total, prev);
      // Morph resets host classes from the server output; restore collapse state.
      this.#applyCollapsed();
    });
  };

  #onToggleClick = (event) => {
    const btn =
      event.target instanceof Element
        ? event.target.closest('[data-eg-gift-progress-toggle]')
        : null;
    if (!btn || !this.contains(btn)) return;
    this.#collapsed = !this.#collapsed;
    this.#storeCollapsed(this.#collapsed);
    this.#applyCollapsed();
  };

  #computeMaxThreshold() {
    let max = 0;
    this.#tierElements().forEach((tier) => {
      const t = parseInt(tier.dataset.thresholdCents || '0', 10);
      if (t > max) max = t;
    });
    this.#maxThresholdCents = max;
  }

  #renderNotches() {
    const track = this.querySelector('[data-eg-gift-progress-track]');
    if (!track || this.#maxThresholdCents <= 0) return;
    // Clear any existing notches (idempotent on re-hydrate).
    track
      .querySelectorAll('[data-eg-gift-progress-notch]')
      .forEach((n) => n.remove());
    this.#tierElements().forEach((tier) => {
      const t = parseInt(tier.dataset.thresholdCents || '0', 10);
      const notch = document.createElement('span');
      notch.className = 'eg-gift-progress__bar-notch';
      notch.setAttribute('data-eg-gift-progress-notch', '');
      notch.setAttribute('data-threshold-cents', String(t));
      notch.setAttribute('aria-hidden', 'true');
      const pct = Math.min(
        100,
        Math.max(0, (t / this.#maxThresholdCents) * 100)
      );
      notch.style.left = `${pct}%`;
      track.appendChild(notch);
    });
  }

  #applyCartTotal(newTotal, prevTotal) {
    this.#renderBarFill(newTotal);
    this.#renderStatusMessage(newTotal);
    this.#renderTiers(prevTotal, newTotal);
    this.#renderAria(newTotal);
    this.#renderAllUnlocked(newTotal);
  }

  #renderBarFill(total) {
    const fill = this.querySelector('[data-eg-gift-progress-fill]');
    if (!fill || this.#maxThresholdCents <= 0) return;
    const pct = Math.min(100, (total / this.#maxThresholdCents) * 100);
    fill.style.width = `${pct}%`;
  }

  #renderStatusMessage(total) {
    const statusEl = this.querySelector('[data-eg-gift-progress-status]');
    if (!statusEl) return;

    if (total >= this.#maxThresholdCents && this.#maxThresholdCents > 0) {
      const allUnlocked = this.#readLocale('all_unlocked');
      if (allUnlocked != null) statusEl.textContent = allUnlocked;
      return;
    }

    let nextThreshold = Infinity;
    let nextLabel = '';
    this.#tierElements().forEach((tier) => {
      const t = parseInt(tier.dataset.thresholdCents || '0', 10);
      if (t > total && t < nextThreshold) {
        nextThreshold = t;
        nextLabel = tier.dataset.label || '';
      }
    });
    if (nextThreshold === Infinity) return;

    const remaining = Math.max(0, nextThreshold - total);
    const template = this.#readLocale('remaining_html');
    if (template == null) return;
    statusEl.innerHTML = this.#interpolate(template, {
      amount: this.#money(remaining),
      label: this.#escape(nextLabel),
    });
  }

  #renderTiers(prevTotal, newTotal) {
    const tiers = this.#tierElements();
    const justUnlocked = [];

    // Identify the next-up tier (lowest threshold still greater than cart total)
    let nextThreshold = Infinity;
    tiers.forEach((tier) => {
      const t = parseInt(tier.dataset.thresholdCents || '0', 10);
      if (t > newTotal && t < nextThreshold) nextThreshold = t;
    });

    tiers.forEach((tier) => {
      const threshold = parseInt(tier.dataset.thresholdCents || '0', 10);
      const wasUnlocked = prevTotal >= threshold;
      const isUnlocked = newTotal >= threshold;
      const remaining = Math.max(0, threshold - newTotal);

      tier.classList.toggle('is-unlocked', isUnlocked);
      tier.classList.toggle(
        'is-next-up',
        nextThreshold !== Infinity && threshold === nextThreshold
      );
      tier.setAttribute('aria-disabled', isUnlocked ? 'false' : 'true');

      const lockedEl = tier.querySelector(
        '[data-eg-gift-progress-tier-locked]'
      );
      if (lockedEl) {
        const template = this.#readLocale('locked_status_html');
        if (template != null) {
          lockedEl.innerHTML = this.#interpolate(template, {
            amount: this.#money(remaining),
          });
        }
      }

      if (isUnlocked && !wasUnlocked) justUnlocked.push(tier);
    });

    justUnlocked.forEach((tier) => this.#celebrateUnlock(tier));
    this.#markNotches(prevTotal, newTotal);
  }

  #markNotches(prevTotal, newTotal) {
    this.querySelectorAll('[data-eg-gift-progress-notch]').forEach((notch) => {
      const t = parseInt(notch.dataset.thresholdCents || '0', 10);
      const wasReached = prevTotal >= t;
      const isReached = newTotal >= t;
      notch.classList.toggle('is-reached', isReached);
      if (isReached && !wasReached) {
        notch.classList.add('is-just-crossed');
        window.setTimeout(() => notch.classList.remove('is-just-crossed'), 600);
      }
    });
  }

  #celebrateUnlock(tier) {
    tier.classList.add('is-just-unlocked');
    window.setTimeout(() => tier.classList.remove('is-just-unlocked'), 950);

    const live = this.querySelector('[data-eg-gift-progress-live]');
    if (!live) return;
    const template = this.#readLocale('unlocked_announcement');
    if (template == null) return;
    live.textContent = this.#interpolate(template, {
      label: tier.dataset.label || '',
    });
  }

  #renderAria(total) {
    const bar = this.querySelector('[data-eg-gift-progress-bar]');
    if (!bar) return;
    bar.setAttribute('aria-valuenow', String(total));
    if (this.#maxThresholdCents > 0) {
      bar.setAttribute('aria-valuemax', String(this.#maxThresholdCents));
    }
    const template = this.#readLocale('aria_value_text');
    if (template == null) return;
    bar.setAttribute(
      'aria-valuetext',
      this.#interpolate(template, {
        current: this.#money(total),
        target: this.#money(this.#maxThresholdCents),
      })
    );
  }

  #renderAllUnlocked(total) {
    const all =
      this.#maxThresholdCents > 0 && total >= this.#maxThresholdCents;
    this.classList.toggle('eg-gift-progress--all-unlocked', all);
  }

  #applyCollapsed() {
    this.classList.toggle('eg-gift-progress--collapsed', this.#collapsed);
    const btn = this.querySelector('[data-eg-gift-progress-toggle]');
    if (btn) {
      btn.setAttribute('aria-expanded', this.#collapsed ? 'false' : 'true');
    }
  }

  #storageKey() {
    return `eg-gift-progress-collapsed:${this.dataset.blockId || 'default'}`;
  }

  #readStoredCollapsed() {
    try {
      return sessionStorage.getItem(this.#storageKey()) === '1';
    } catch (_err) {
      return false;
    }
  }

  #storeCollapsed(collapsed) {
    try {
      sessionStorage.setItem(this.#storageKey(), collapsed ? '1' : '0');
    } catch (_err) {
      /* storage unavailable (private mode / disabled) — non-fatal */
    }
  }

  #tierElements() {
    return Array.from(this.querySelectorAll('[data-eg-gift-progress-tier]'));
  }

  #money(cents) {
    if (!this.#moneyFormatter) return `${(cents / 100).toFixed(2)}`;
    return this.#moneyFormatter.format(cents / 100);
  }

  #readLocale(key) {
    if (this.#localeCache[key] != null) return this.#localeCache[key];
    const attr = this.getAttribute(`data-locale-${key.replace(/_/g, '-')}`);
    if (attr == null) return null;
    this.#localeCache[key] = attr;
    return attr;
  }

  #interpolate(template, vars) {
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key) =>
      Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : ''
    );
  }

  #escape(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('eg-gift-progress-bar')) {
  customElements.define('eg-gift-progress-bar', EgGiftProgressBar);
}
